import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { uploadSmart } from "@/lib/upload";
import { UploadProgress, dropProps } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Paperclip, X, FileText, Send } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["Fuar Standı", "Dekor & Tasarım", "Etkinlik Alanı", "Sahne & Prodüksiyon", "Diğer"];

export default function CustomerProjectCreatePage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", category: "", location: "", event_date: "", description: "" });
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [progress, setProgress] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileInput = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    try {
      const lead = JSON.parse(sessionStorage.getItem("gp_lead") || "null");
      if (lead?.message) setForm((f) => ({ ...f, description: lead.message }));
    } catch {}
  }, []);

  const onFiles = async (e) => {
    const list = Array.from(e?.target?.files || e || []);
    if (list.length === 0) return;
    setUploading(true);
    try {
      const data = await uploadSmart(list, setProgress);
      setFiles((f) => [...f, ...data]);
    } catch (err) {
      toast.error(err?.message || apiError(err));
    } finally {
      setUploading(false);
      setProgress(null);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const submit = async () => {
    if (form.name.trim().length < 2) return toast.error("Proje adı girin");
    if (!form.category) return toast.error("Kategori seçin");
    setBusy(true);
    try {
      await api.post("/customer/projects", {
        name: form.name, category: form.category, location: form.location,
        event_date: form.event_date || null, description: form.description,
        file_ids: files.map((f) => f.id),
      });
      sessionStorage.removeItem("gp_lead");
      toast.success("Projeniz oluşturuldu! Ekibimiz teklif sürecini başlatacak.");
      navigate("/musteri/projeler");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8 fade-in max-w-2xl">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Yeni Proje</div>
        <h1 className="font-heading text-4xl font-extrabold tracking-tighter mt-1" data-testid="customer-project-create-title">Projenizi Oluşturun</h1>
        <p className="text-sm text-slate-500 mt-2">Projenizi anlatın; ekibimiz tedarikçi havuzundan teklifler toplasın, siz karşılaştırın.</p>
      </div>

      <div className="border border-slate-200 bg-white p-6 lg:p-8 space-y-5">
        <div className="space-y-1.5">
          <Label htmlFor="cp-name">Proje Adı *</Label>
          <Input id="cp-name" data-testid="customer-project-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="örn. İstanbul Expo 2026 Standımız" className="rounded-none bg-[#F5F7FA] border-slate-200 h-11" />
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <div className="space-y-1.5">
            <Label>Kategori *</Label>
            <Select value={form.category} onValueChange={(v) => { if (v) set("category", v); }}>
              <SelectTrigger className="rounded-none bg-[#F5F7FA] border-slate-200 h-11" data-testid="customer-project-category">
                <SelectValue placeholder="Seçin" />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-200 rounded-none">
                {CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c} data-testid={`category-option-${c}`}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cp-date">Etkinlik Tarihi</Label>
            <Input id="cp-date" data-testid="customer-project-date" type="date" value={form.event_date} onChange={(e) => set("event_date", e.target.value)} className="rounded-none bg-[#F5F7FA] border-slate-200 h-11" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="cp-location">Lokasyon</Label>
          <Input id="cp-location" data-testid="customer-project-location" value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="örn. İstanbul Fuar Merkezi" className="rounded-none bg-[#F5F7FA] border-slate-200 h-11" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="cp-desc">Proje Açıklaması</Label>
          <Textarea id="cp-desc" data-testid="customer-project-description" value={form.description} onChange={(e) => set("description", e.target.value)} rows={6} placeholder="Ölçüler, beklentiler, bütçe aralığı, özel istekler..." className="rounded-none bg-[#F5F7FA] border-slate-200 text-sm" />
        </div>
        <div className="space-y-1.5">
          <Label>Dosya Ekle (opsiyonel)</Label>
          <input ref={fileInput} type="file" multiple className="hidden" onChange={onFiles} data-testid="customer-project-file-input" />
          <button type="button" onClick={() => fileInput.current?.click()} {...dropProps(onFiles, setDragOver)} data-testid="customer-project-dropzone" className={`w-full border border-dashed transition-colors duration-200 p-6 flex flex-col items-center gap-2 ${dragOver ? "border-slate-900 bg-slate-100 text-slate-900" : "border-slate-300 bg-[#F5F7FA] hover:border-slate-400 text-slate-500"}`}>
            <Paperclip size={18} />
            <span className="text-sm">{uploading ? "Yükleniyor..." : dragOver ? "Dosyaları buraya bırakın" : "Çizim, brief veya görsellerinizi ekleyin ya da sürükleyip bırakın"}</span>
          </button>
          {uploading && <UploadProgress progress={progress} />}
          {files.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-2">
              {files.map((f) => (
                <div key={f.id} className="flex items-center gap-2 border border-slate-200 bg-[#F5F7FA] px-3 py-2 text-xs">
                  <FileText size={13} />
                  <span className="max-w-[160px] truncate">{f.original_filename}</span>
                  <button onClick={() => setFiles((arr) => arr.filter((x) => x.id !== f.id))} className="text-slate-500 hover:text-red-600 transition-colors duration-200">
                    <X size={13} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <Button onClick={submit} disabled={busy || uploading} data-testid="customer-project-submit" className="rounded-none h-12 px-8 bg-slate-900 text-white hover:bg-slate-700 font-semibold gap-2 transition-colors duration-200">
          <Send size={15} /> {busy ? "Oluşturuluyor..." : "Projeyi Oluştur"}
        </Button>
      </div>
    </div>
  );
}
